"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Code2,
  Sparkles,
  Target,
  Building2,
  Layers,
  ArrowRight,
  Crown,
  Mic,
  Activity,
  Cpu,
  ShieldCheck,
  TrendingUp,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { StepperBlock } from "@/lib/blocks/stepper-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "34→94", suffix: "%", label: "Policy Implementation", sub: "Improvement rate", accent: "emerald" },
  { metric: "42→89", suffix: "%", label: "Citizen Satisfaction", sub: "Elevation rate", accent: "cyan" },
  { metric: "120", suffix: "M", label: "Citizens per Deployment", sub: "Single deployment scale", accent: "purple" },
  { metric: "200", suffix: "+", label: "Organizations", sub: "Worldwide deployments", accent: "amber-gold" },
];

const STACK = [
  { layer: "L1", title: "Abstraction", body: "Schema abstraction, protocol translation, transaction coordination, sovereignty enforcement. Citizen data never leaves national jurisdiction." },
  { layer: "L2", title: "Service", body: "Federated identity, multi-channel notification, workflow engine, secure API gateway. Common capabilities across all applications." },
  { layer: "L3", title: "Orchestration", body: "Cross-domain workflow templates, resource pooling, dependency detection, escalation routing. Coordinates across departmental boundaries." },
  { layer: "L4", title: "Application", body: "Policy development, administration management, service delivery, engagement suite. Domain-specific functionality for governance operations." },
  { layer: "L5", title: "Presentation", body: "Responsive web, native mobile, government kiosk, IVR voice interface. Citizen-facing accessibility and adoption." },
  { layer: "L6", title: "Integration", body: "Enterprise service bus, message queue bridges, batch file interfaces, database connectors. The connectivity fabric that eliminates silos." },
];

const CAPABILITIES = [
  { num: "01", title: "Predictive Policy Modeling", body: "Monte Carlo simulation across thousands of scenarios. The platform tests policy permutations against historical governance data from eighteen countries.", accent: "red" },
  { num: "02", title: "Natural Language Processing", body: "Processes citizen communications across fifteen-plus languages. NLP-based sentiment categorization routes feedback to relevant departments with priority scoring.", accent: "purple" },
  { num: "03", title: "Computer Vision", body: "Infrastructure monitoring through satellite and drone imagery. Detecting road damage, construction violations, environmental changes that field inspection cannot cover at scale.", accent: "cyan" },
  { num: "04", title: "Anomaly Detection", body: "Machine learning models identify fraud, waste, and abuse. Four detection modalities. Ninety-five percent true positive rate with under five percent false positives.", accent: "emerald" },
];

const DOMAINS = [
  { title: "Sovereign-Scale Digital Governance", body: "Complete digitization of government operations — legislative intelligence through citizen service delivery. Powered by the eGovernance platform and the governance intelligence platform.", accent: "red" },
  { title: "Enterprise Digital Modernization", body: "AI-driven intelligence, automated decision support, integrated platform ecosystems. Deployed at 50+ Fortune 500 companies. 450% financial ROI with $12M annual savings.", accent: "purple" },
  { title: "Technology Sovereignty", body: "Zero third-party dependencies, proprietary platforms, sovereign-grade security, air-gap deployment capability. The security posture that makes digital transformation possible at the level where the stakes are national.", accent: "cyan" },
];

export default function DigitalTransformationPage() {
  return (
    <>
      <PageHero
        icon={Code2}
        overline="Digital Transformation"
        overlineClassName="text-amber-gold"
        title={
          <>
            Engineering the <span className="text-gradient-premium">Digital Future.</span>
          </>
        }
        subtitle="Digital transformation is not a technology project. It is a civilizational shift. Organizations that treat it as an IT upgrade fail. Organizations that treat it as an operating system — engineered, orchestrated, and governed with the same precision that medicine demands of a surgeon — succeed."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Digital Transformation" }]}
        badge="The operating system for statecraft at national scale"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <Sparkles className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">The Digital Imperative</span>
            </div>
            <h2 className="text-display-md mb-5">
              The question is not whether to transform. It is whether to survive.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Governments worldwide operate at pre-digital efficiency. Seventy-three percent of processes remain paper-based. Sixty-seven percent of citizens cannot access services easily. Only thirty-four percent of policies achieve full implementation. The cost of this inefficiency is measured in citizen trust eroded, public resources wasted, and democratic legitimacy undermined.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The digital transformation capability Dr. Jyoti Kush oversees addresses the four crises simultaneously: the governance crisis, the security vacuum, the intelligence deficit, and the integration gap. Not with advice. With systems. Not with projections. With verified outcomes.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Six-Layer Technology Stack</span>
          </div>
          <h2 className="text-display-md mb-4">Six integrated layers. Sovereign-grade architecture.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The architecture spans six integrated layers, each designed for sovereign-grade operations where the margin for error is zero and the stakes are national.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {STACK.map((s, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={s.layer}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-caption font-bold ${a.text} opacity-50 mb-2`}>{s.layer}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{s.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Cpu className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The AI & Machine Learning Layer</span>
          </div>
          <h2 className="text-display-md mb-4">The intelligence layer that transforms governance.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            AI is not a feature. It is the intelligence layer that makes transformation intelligent — converting raw data into predictive insight, automating routine decisions, and augmenting human judgment.
          </p>
        </div>
        <div className="space-y-3">
          {CAPABILITIES.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-caption font-bold text-white">{c.num}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{c.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Target className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Three Application Domains</span>
          </div>
          <h2 className="text-display-md mb-4">From sovereign governance to enterprise modernization.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The digital transformation capability spans three interconnected domains, each with verified outcomes at scale.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {DOMAINS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{d.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="transformation-steps" ariaLabel="Transformation Steps">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Target className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Transformation Steps</span>
          </div>
          <h2 className="text-display-md mb-4">Six steps from assessment to sovereign-grade deployment.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature vertical progression. Every digital transformation engagement follows this battle-tested sequence. The steps are sequential in logic but iterative in practice.
          </p>
        </div>
        <StepperBlock
          orientation="vertical"
          steps={[
            { num: "01", title: "Strategic Assessment", body: "Comprehensive audit of existing digital infrastructure, process maturity, governance capability, and citizen experience baseline. The diagnostic phase. Every transformation begins with diagnosis.", icon: <Activity className="size-4" />, accent: "red" },
            { num: "02", title: "Architecture Design", body: "Blueprint for the transformed operating model including technology, process, organizational, and governance dimensions. The system architecture. The design that will be built.", icon: <Layers className="size-4" />, accent: "purple" },
            { num: "03", title: "Platform Build", body: "Platform configuration, customization, integration, and deployment across all departments. The operational infrastructure — nine proprietary platforms composed into a unified operating system.", icon: <Cpu className="size-4" />, accent: "cyan" },
            { num: "04", title: "Phased Rollout", body: "Phased deployment across departments, regions, and tiers with continuous monitoring. Citizen migration campaigns. Issue resolution. The discipline of staged deployment that prevents transformation shock.", icon: <Building2 className="size-4" />, accent: "emerald" },
            { num: "05", title: "Capability Optimization", body: "Performance measurement, user feedback integration, process refinement, and continuous improvement cycles. The closed loop. The system that improves with every cycle.", icon: <TrendingUp className="size-4" />, accent: "amber-gold" },
            { num: "06", title: "Sovereign Evolution", body: "Platform evolution through new service introduction, policy adaptation, technology upgrade, and scale expansion. The transformation is durable. The outcome is sustainable. The record is verified.", icon: <ShieldCheck className="size-4" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Cpu className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Integration Advantage</span>
          </div>
          <h2 className="text-display-md mb-4">Six forces that compound in the integrated system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The forces that distinguish a digital transformation that produces outcomes from a digital transformation that produces demos. The integration is the moat.
          </p>
        </div>
        <PillarGridBlock
          columns={3}
          variant="card"
          pillars={[
            { title: "Single Pane of Glass", body: "Nine platforms operating as one. The command interface that unifies intelligence, perception, content, ground truth, governance, crisis, security, and citizen engagement. The integration is the moat.", icon: <Layers className="size-5" />, accent: "red" },
            { title: "Cross-Domain Correlation", body: "Intelligence from one domain informs action in another. A security event triggers perception containment. A governance metric triggers intelligence adjustment. The correlation is the capability.", icon: <Activity className="size-5" />, accent: "purple" },
            { title: "Real-Time Decision Support", body: "Decision time compressed from twenty-four to seventy-two hours to under one hour. The capability that makes sovereign-scale operations possible at the tempo of contemporary events.", icon: <Target className="size-5" />, accent: "cyan" },
            { title: "Sovereign Data Layer", body: "National data residency. Citizen data sovereignty. Cross-border data flow governance. The integration is meaningful only when the data layer respects sovereignty.", icon: <ShieldCheck className="size-5" />, accent: "emerald" },
            { title: "Continuous AI Refinement", body: "The system that enters the next engagement is smarter than the one that entered the last. Every outcome feeds back into the models. The advantage compounds.", icon: <Cpu className="size-5" />, accent: "amber-gold" },
            { title: "Outcome Verification Layer", body: "Every claim is grounded in verified operational data. The integration includes the discipline of measurement. The transformation is not claimed. It is verified.", icon: <TrendingUp className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <TrendingUp className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Verified Outcomes</span>
          </div>
          <h2 className="text-display-md mb-4">The transformation record.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data. The record is not aspirational. It is the verified performance of an organization that has been doing sovereign-scale digital transformation for fifteen-plus years.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 34, suffix: "→94", prefix: "", label: "Policy Implementation %", description: "Improvement from baseline to deployment", accent: "emerald" },
            { value: 42, suffix: "→89", prefix: "", label: "Citizen Satisfaction %", description: "Elevation across deployments", accent: "cyan" },
            { value: 120, suffix: "M", label: "Citizens Served", description: "Single deployment scale", accent: "purple" },
            { value: 200, suffix: "+", label: "Organizations Deployed", description: "Including fifty-plus Fortune 500 companies", accent: "amber-gold" },
            { value: 620, suffix: "%", label: "Federal Government ROI", description: "Verified return on engagement investment", accent: "red" },
            { value: 9, label: "Proprietary Platforms", description: "Zero third-party dependencies", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engineer the digital future.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Sovereign-scale digital transformation for governments, Fortune 500 corporations, and international organizations. Every engagement begins with assessment. Every outcome verified.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Code2, accent: "amber-gold" },
          { title: "Governance & Public Policy", description: "Converting authority into outcomes. eGovernance, citizen engagement, policy implementation.", href: "/expertise/governance-public-policy/", icon: Building2, accent: "emerald" },
          { title: "AI, Technology & The Future", description: "Governing intelligence at sovereign scale. The AI architecture the future demands.", href: "/expertise/ai-technology-future/", icon: Cpu, accent: "purple" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "cyan" },
        ]}
      />
    </>
  );
}
